import type {
  DealResourcePlan,
  EstimateGroundingRef,
  EstimateHoursOverride,
  EstimateRange,
  EstimateVersionSummary,
  ResourcePlanPricingBasis,
  SalesDealEstimateResult,
  SalesEstimateFlag,
  SalesEstimateRuleApplication,
  SalesEstimateSectionBreakdown,
  SalesEstimateSubsectionBreakdown,
  SavedDealEstimate,
} from '@protoss/shared';
import {
  estimationMethodLabel,
  getEffectiveEstimateData,
  provenanceDetail,
  workTypeLabel,
} from '@protoss/shared';
import { Cross1Icon, FileTextIcon, Select, SelectItem, SkeletonBlock, Tooltip } from '@protoss/ui';
import * as React from 'react';
import { ConfidenceBadge } from '../../../components/shared/sales/ConfidenceBadge';
import { formatDateTimeWithYear } from '../../../lib/datetime';
import { computeHoursRange } from '../utils/estimateHours';
import { formatDealVersionLabel } from './versioning';
import type { ResourcePlanAvailableRole } from '../services/salesApi';
import { ReferenceEstimateDialog } from './ReferenceEstimateDialog';
import { ResourceInvestmentTable } from './ResourceInvestmentTable';
import { ResourcingModelSummary } from './ResourcingModelSummary';

// Temporarily hide the version-history dropdown in the estimate UI. Flip to
// `true` to restore it (the version plumbing below stays wired).
const SHOW_VERSION_DROPDOWN = false;

export interface DealEstimatePaneProps {
  result: SalesDealEstimateResult | null;
  loading: boolean;
  error: string | null;
  isReady: boolean;
  isStale: boolean;
  missingRequiredCount: number;
  savedEstimate: SavedDealEstimate | null;
  resourcePlan: DealResourcePlan | null;
  /**
   * Called when the user applies or clears a section-level hours override.
   * key follows "section:{sectionKey}". Pass null for overrideHours to clear.
   * When omitted the estimate is rendered read-only (no inline edit controls).
   */
  onSectionOverride?: (key: string, overrideHours: number | null) => void | Promise<void>;
  /**
   * Called when the user changes the free-text override reason for a subsection.
   * key follows "subsection:{sectionKey}:{subsectionKey}".
   * Only called when an active override exists for the key.
   */
  onSectionOverrideReason?: (key: string, reason: string) => void | Promise<void>;
  /**
   * Called when the user inline-edits a subsection's AI summary text (client-facing or technical).
   * key follows "subsection:{sectionKey}:{subsectionKey}". When omitted the summary is read-only.
   */
  onSubsectionSummaryEdit?: (key: string, patch: SummaryPatch) => void | Promise<void>;
  /**
   * AI "Suggest" for a subsection summary; resolves to the suggested text, or null on failure
   * (the caller keeps the user's draft). Required (with the edit handler) for inline summary editing.
   */
  onSubsectionSummarySuggest?: (
    key: string,
    kind: 'customer' | 'ai',
    text: string
  ) => Promise<string | null>;
  /**
   * Called when the user overrides the discount percentage for this deal.
   * Pass null to clear the override and use the workspace default.
   */
  onDiscountOverride?: (discountPercentageOverride: number | null) => void | Promise<void>;
  /**
   * Called when the user changes the pricing basis for a resource role. For the
   * `hybrid` basis, `offshorePercent` (0–100) carries the chosen offshore share.
   */
  onUpdateRolePricingBasis?: (
    roleId: string,
    pricingBasis: ResourcePlanPricingBasis,
    offshorePercent?: number
  ) => void;
  updatingRolePricingBasisId?: string | null;
  /**
   * Called when the user overrides a role's allocation % in the investment table.
   * `allocationPercent` is 0–100, or `null` to clear the override.
   */
  onUpdateRoleAllocation?: (roleId: string, allocationPercent: number | null) => void;
  updatingRoleAllocationId?: string | null;
  /** Workspace role catalog for the add/remove role multi-select. */
  availableRoles?: ResourcePlanAvailableRole[];
  /** Set the full desired role roster (current roles +/- the user's edits). */
  onRoleSelectionChange?: (roleIds: string[]) => void;
  /** True while a roster change is saving. */
  updatingRoles?: boolean;
  /** Rate card profiles available for regenerating the resource plan / rate table. */
  availableVersions?: Array<{ id: number; version: string; isDefault: boolean; scope: string }>;
  /** Regenerate the resource plan, optionally against a specific rate card profile. */
  onRegenerate?: (rateCardProfileId?: number) => void;
  regenerating?: boolean;
  /** Read-only version history for the dropdown (newest first); current is flagged. */
  versions?: EstimateVersionSummary[];
  /** Currently-selected version uuid (null/undefined = current/working version). */
  selectedVersionUuid?: string | null;
  /** Select a version to view read-only (and export). */
  onSelectVersion?: (uuid: string) => void;
  /** True when a non-current version is being viewed (drives the read-only banner). */
  viewingPastVersion?: boolean;
}

export interface DealEstimateListPaneProps {
  estimateResult: SalesDealEstimateResult | null;
  loading: boolean;
  savedEstimate: SavedDealEstimate | null;
  isCurrent: boolean;
  onViewEstimate: () => void;
}

function hoursRangeNode(hours: number, range?: EstimateRange | null): React.ReactNode | null {
  const r = computeHoursRange(hours, range);
  if (!r) return null;
  return (
    <span className="sales-estimate-pane__hours-range">
      {r.low.toLocaleString()}h{'\u2009'}|{'\u2009'}{r.high.toLocaleString()}h
    </span>
  );
}

function renderHours(hours: number, range?: EstimateRange | null): React.ReactNode {
  const base = `${hours.toLocaleString()}h`;
  const rangeNode = hoursRangeNode(hours, range);
  if (!rangeNode) return base;
  return (
    <span className="sales-estimate-pane__hours-stack">
      <span>{base}</span>
      {rangeNode}
    </span>
  );
}

function formatInputValue(value: unknown): string {
  if (value === null || value === undefined) return '\u2014';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    if (value.length === 0) return '(empty)';
    return (value as unknown[]).map((v) => String(v)).join(', ');
  }
  return JSON.stringify(value);
}

/**
 * Inputs are keyed by qualified field key, or prefixed `cfg:` (a config constant)
 * / `sig:` (a signal total). Strip the prefix for display and surface the source
 * as a small tag so a config constant isn't mistaken for a questionnaire answer.
 */
function describeInputKey(key: string): { label: string; kind: 'field' | 'config' | 'signal' } {
  if (key.startsWith('cfg:')) return { label: key.slice(4), kind: 'config' };
  if (key.startsWith('sig:')) return { label: key.slice(4), kind: 'signal' };
  return { label: key, kind: 'field' };
}

function InputRows({ inputs }: { inputs: Record<string, unknown> }) {
  return (
    <div className="sales-estimate-pane__rule-detail-inputs">
      {Object.entries(inputs).map(([key, value]) => {
        const { label, kind } = describeInputKey(key);
        return (
          <div key={key} className="sales-estimate-pane__rule-detail-input-row">
            <code className="sales-estimate-pane__rule-detail-input-key">
              {label}
              {kind !== 'field' ? (
                <span
                  className={`sales-estimate-pane__rule-detail-input-tag sales-estimate-pane__rule-detail-input-tag--${kind}`}
                >
                  {kind}
                </span>
              ) : null}
            </code>
            <span className="sales-estimate-pane__rule-detail-input-value">
              {formatInputValue(value)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function summarizeFlags(flags: SalesEstimateFlag[]): string[] {
  return flags.slice(0, 3).map((flag) => flag.message);
}

function renderEmptyState(props: {
  title: string;
  body: string;
  tone: 'neutral' | 'warning' | 'danger';
}) {
  return (
    <div className={`sales-estimate-pane__empty sales-estimate-pane__empty--${props.tone}`}>
      <div className="sales-estimate-pane__empty-icon" aria-hidden="true">
        <FileTextIcon width={18} height={18} />
      </div>
      <div className="sales-estimate-pane__empty-copy">
        <h3 className="sales-estimate-pane__empty-title">{props.title}</h3>
        <p className="sales-estimate-pane__empty-body">{props.body}</p>
      </div>
    </div>
  );
}

// ── Inline hours override editor ──────────────────────────────────────────────

/**
 * Renders either:
 *  - A plain <strong> (read-only mode, no onSave)
 *  - A clickable <button> that enters edit mode on click (no override active)
 *  - An overridden value badge + clear button (override active)
 *  - An inline <input> (edit mode)
 *
 * Low/upper range values are always derived from the effective hours and the
 * estimateRange percentages - they are never directly editable.
 */
function SectionHoursEdit({
  hours,
  estimateRange,
  override,
  onSave,
}: {
  hours: number;
  estimateRange?: EstimateRange | null;
  override: EstimateHoursOverride | undefined;
  /** When undefined the widget is read-only. */
  onSave?: (newHours: number | null) => void | Promise<void>;
}) {
  const [editing, setEditing] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [pendingHours, setPendingHours] = React.useState<number | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const effectiveHours = override ? override.overrideHours : hours;

  function startEdit(e: React.MouseEvent) {
    e.stopPropagation();
    if (!onSave || isSaving) return;
    setInputValue(String(effectiveHours));
    setEditing(true);
  }

  async function commit() {
    const n = parseInt(inputValue, 10);
    setEditing(false);
    if (!isNaN(n) && n >= 0 && n !== effectiveHours && onSave) {
      setPendingHours(n);
      setIsSaving(true);
      try {
        await onSave(n);
      } finally {
        setIsSaving(false);
        setPendingHours(null);
      }
    }
  }

  function cancel() {
    setEditing(false);
  }

  async function clearOverride(e: React.MouseEvent) {
    e.stopPropagation();
    if (!onSave || isSaving) return;
    setPendingHours(null);
    setIsSaving(true);
    try {
      await onSave(null);
    } finally {
      setIsSaving(false);
    }
  }

  React.useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // DB round-trip in progress - show pending value + spinner
  if (isSaving) {
    const displayHours = pendingHours !== null ? pendingHours : hours;
    return (
      <div className="sales-estimate-pane__hours-saving" aria-busy="true">
        <span className="sales-estimate-pane__hours-saving-value">
          {renderHours(displayHours, estimateRange)}
        </span>
        <span className="sales-estimate-pane__hours-spinner" aria-label="Saving…" />
      </div>
    );
  }

  if (editing) {
    return (
      <div
        className="sales-estimate-pane__hours-edit"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <input
          ref={inputRef}
          type="number"
          min="0"
          step="1"
          className="sales-estimate-pane__hours-input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void commit();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          onBlur={() => void commit()}
          aria-label="Override section hours"
        />
        <span className="sales-estimate-pane__hours-input-suffix" aria-hidden="true">
          h
        </span>
      </div>
    );
  }

  if (override) {
    const rangeNode = hoursRangeNode(effectiveHours, estimateRange);
    return (
      <div className="sales-estimate-pane__hours-overridden">
        <span className={rangeNode ? 'sales-estimate-pane__hours-stack' : undefined}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button
                type="button"
                className="sales-estimate-pane__hours-value sales-estimate-pane__hours-value--overridden"
                onClick={onSave ? startEdit : undefined}
                aria-label={`Overridden to ${effectiveHours}h (original ${override.originalHours}h)${onSave ? '. Click to edit.' : ''}`}
              >
                {effectiveHours.toLocaleString()}h
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content side="top" sideOffset={4} className="ui-tooltip">
                Original: {renderHours(override.originalHours, estimateRange)}
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
          {rangeNode}
        </span>
        {onSave ? (
          <Tooltip.Simple label="Reset to calculated value">
            <button
              type="button"
              className="sales-estimate-pane__hours-clear"
              onClick={(e) => void clearOverride(e)}
              aria-label="Reset to calculated value"
            >
              <Cross1Icon width={8} height={8} />
            </button>
          </Tooltip.Simple>
        ) : null}
      </div>
    );
  }

  if (!onSave) {
    return (
      <strong className="sales-estimate-pane__section-hours">
        {renderHours(effectiveHours, estimateRange)}
      </strong>
    );
  }

  const rangeNode = hoursRangeNode(effectiveHours, estimateRange);
  return (
    <span className={rangeNode ? 'sales-estimate-pane__hours-stack' : undefined}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            type="button"
            className="sales-estimate-pane__hours-value"
            onClick={startEdit}
            aria-label={`${effectiveHours.toLocaleString()}h - click to override`}
          >
            {effectiveHours.toLocaleString()}h
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content side="top" sideOffset={4} className="ui-tooltip">
            Click to override hours
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
      {rangeNode}
    </span>
  );
}

// ── Rule detail panel ─────────────────────────────────────────────────────────

function EstimateRuleDetail({
  rule,
  onClose,
}: {
  rule: SalesEstimateRuleApplication;
  onClose: () => void;
}) {
  const hasInputs = rule.inputs ? Object.keys(rule.inputs).length > 0 : false;
  return (
    <div className="sales-estimate-pane__rule-detail">
      <div className="sales-estimate-pane__rule-detail-meta">
        <code className="sales-estimate-pane__rule-detail-key">{rule.ruleKey}</code>
        <button
          type="button"
          className="sales-estimate-pane__rule-detail-close"
          onClick={onClose}
          aria-label="Close rule detail"
        >
          <Cross1Icon width={10} height={10} />
        </button>
      </div>

      {rule.calcSummary ? (
        <div className="sales-estimate-pane__rule-detail-section">
          <span className="sales-estimate-pane__rule-detail-section-label">Calculation</span>
          <span className="sales-estimate-pane__rule-detail-expr">{rule.calcSummary}</span>
        </div>
      ) : null}

      {rule.signalDerivations && rule.signalDerivations.length > 0 ? (
        <div className="sales-estimate-pane__rule-detail-section">
          <span className="sales-estimate-pane__rule-detail-section-label">Derivation</span>
          {rule.signalDerivations.map((d) => (
            <div key={d.signalKey} className="sales-estimate-pane__rule-detail-derivation">
              <div className="sales-estimate-pane__rule-detail-derivation-head">
                <span className="sales-estimate-pane__rule-detail-derivation-label">{d.label}</span>
                <span className="sales-estimate-pane__rule-detail-derivation-value">
                  {d.value}h
                </span>
              </div>
              {d.calcSummary ? (
                <span className="sales-estimate-pane__rule-detail-expr">{d.calcSummary}</span>
              ) : null}
              {d.inputs ? <InputRows inputs={d.inputs} /> : null}
            </div>
          ))}
        </div>
      ) : null}

      {rule.conditionSummary ? (
        <div className="sales-estimate-pane__rule-detail-section">
          <span className="sales-estimate-pane__rule-detail-section-label">Applied when</span>
          <span className="sales-estimate-pane__rule-detail-expr">{rule.conditionSummary}</span>
        </div>
      ) : null}

      {hasInputs && rule.inputs ? (
        <div className="sales-estimate-pane__rule-detail-section">
          <span className="sales-estimate-pane__rule-detail-section-label">Field inputs</span>
          <InputRows inputs={rule.inputs} />
        </div>
      ) : null}
    </div>
  );
}

// ── Subsection row ────────────────────────────────────────────────────────────

/** One aligned label -> value row in the Raw Output definition list (renders nothing if empty). */
function RawRow({
  label,
  children,
  valueClass,
}: {
  label: string;
  children: React.ReactNode;
  valueClass?: string;
}) {
  return (
    <>
      <dt className="sales-estimate-pane__rawgrid-label">{label}</dt>
      <dd
        className={['sales-estimate-pane__rawgrid-value', valueClass].filter(Boolean).join(' ')}
      >
        {children}
      </dd>
    </>
  );
}

/**
 * The Raw Output detail body for a line item: an aligned label -> value definition list (Method +
 * grounded [N] refs, Work type, Drivers, In/Out of scope, Assumptions) over a divided Breakdown
 * block (the hours that sum to the line item). Calm dot-separated values, not chips. Renders
 * nothing when there is no detail (rule-engine items fall through to the fired-rules list).
 */
function EstimateProvenance({
  subsection,
  onRefClick,
}: {
  subsection: SalesEstimateSubsectionBreakdown;
  onRefClick: (ref: EstimateGroundingRef) => void;
}) {
  const composition = subsection.hoursComposition ?? [];
  const scope = subsection.scopeMeta;
  const drivers = scope?.complexityDrivers ?? [];
  const inScope = scope?.inScope ?? [];
  const outOfScope = scope?.outOfScope ?? [];
  const assumptions = scope?.assumptions ?? [];
  const method = subsection.estimationMethod;
  const refs = subsection.groundingRefs ?? [];

  const detail = method
    ? provenanceDetail({
        method,
        pastItemCount: refs.length || undefined,
        rangeLow: subsection.rangeLow,
        rangeHigh: subsection.rangeHigh,
      })
    : null;

  const hasGrid =
    !!method ||
    !!scope?.workType ||
    drivers.length > 0 ||
    inScope.length > 0 ||
    outOfScope.length > 0 ||
    assumptions.length > 0;

  if (!hasGrid && composition.length === 0 && refs.length === 0) return null;

  const totalHours = composition.reduce((sum, c) => sum + c.hours, 0);

  return (
    <>
      {hasGrid ? (
        <dl className="sales-estimate-pane__rawgrid">
          {method ? (
            <RawRow label="Method">
              <span>{estimationMethodLabel(method)}</span>
              {detail ? <span>{` · ${detail}`}</span> : null}
            </RawRow>
          ) : null}
          {/* Confidence is surfaced only for items whose grounding was projected (an
              estimationMethod is present) - the stamped value, never a client-side guess. */}
          {method ? (
            <RawRow label="Confidence">
              <ConfidenceBadge
                level={subsection.confidence}
                tooltip="How confident the AI is in this figure, based on how it was grounded"
              />
            </RawRow>
          ) : null}
          {scope?.workType ? (
            <RawRow label="Work type">{workTypeLabel(scope.workType)}</RawRow>
          ) : null}
          {drivers.length > 0 ? <RawRow label="Drivers">{drivers.join(' · ')}</RawRow> : null}
          {inScope.length > 0 ? <RawRow label="In scope">{inScope.join(' · ')}</RawRow> : null}
          {outOfScope.length > 0 ? (
            <RawRow label="Out of scope" valueClass="sales-estimate-pane__rawgrid-value--out">
              {outOfScope.join(' · ')}
            </RawRow>
          ) : null}
          {assumptions.length > 0 ? (
            <RawRow label="Assumptions">
              <ul className="sales-estimate-pane__assumptions">
                {assumptions.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </RawRow>
          ) : null}
        </dl>
      ) : null}

      {/* Grounded source references - a labeled, clickable list (like the chat's "N sources"); each
          opens the same reference-estimate dialog. Only present on reference-corpus-grounded items. */}
      {refs.length > 0 ? (
        <div className="sales-estimate-pane__refs-block">
          <div className="sales-estimate-pane__refs-head">References</div>
          {refs.map((ref, i) => (
            <button
              key={ref.documentUuid}
              type="button"
              className="sales-estimate-pane__ref-row"
              onClick={() => onRefClick(ref)}
              title={ref.sourceLabel ?? ref.title ?? 'Reference estimate'}
            >
              <span className="sales-estimate-pane__ref-row-index">[{i + 1}]</span>
              <span className="sales-estimate-pane__ref-row-label">
                {ref.sourceLabel ?? ref.title ?? 'Reference estimate'}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {composition.length > 0 ? (
        <div className="sales-estimate-pane__breakdown">
          <div className="sales-estimate-pane__breakdown-head">Breakdown</div>
          {composition.map((c, i) => (
            <div key={i} className="sales-estimate-pane__breakdown-row">
              <span className="sales-estimate-pane__breakdown-label">{c.label}</span>
              <span className="sales-estimate-pane__breakdown-hours">{c.hours}h</span>
            </div>
          ))}
          {composition.length > 1 ? (
            <div className="sales-estimate-pane__breakdown-row sales-estimate-pane__breakdown-total">
              <span className="sales-estimate-pane__breakdown-label">Total</span>
              <span className="sales-estimate-pane__breakdown-hours">{totalHours}h</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/** Patch shape for a subsection summary inline-edit (omit a field to leave it unchanged). */
export type SummaryPatch = { customerSummary?: string | null; aiSummary?: string | null };
/** Returns the suggested text, or null on failure (the caller keeps the user's draft). */
export type SummarySuggest = (kind: 'customer' | 'ai', text: string) => Promise<string | null>;

const AUTOSAVE_DELAY_MS = 1500;
const SAVED_HINT_MS = 1500;

type AutosaveStatus = 'idle' | 'saving' | 'saved';

/**
 * Debounced auto-save for an inline-editable text field. Saves AUTOSAVE_DELAY_MS after the last
 * keystroke and immediately on blur, only when the trimmed draft differs from the upstream value.
 * Re-syncs the draft when `value` changes externally (DB round-trip / regenerate). Exposes a save
 * `status` for the subtle "Saving…/Saved" hint, and flushes a pending edit on unmount so collapsing
 * the row or navigating away never drops an in-progress edit. Used for both the AI summary and the
 * override reason.
 */
function useAutosaveText(value: string, onSave: (text: string) => void | Promise<void>) {
  const [draft, setDraft] = React.useState(value);
  const [status, setStatus] = React.useState<AutosaveStatus>('idle');
  const saveTimer = React.useRef<number | null>(null);
  const hintTimer = React.useRef<number | null>(null);
  // Latest-value refs so the unmount flush + timers read current state without re-subscribing.
  const mounted = React.useRef(true);
  const valueRef = React.useRef(value);
  const draftRef = React.useRef(value);
  const onSaveRef = React.useRef(onSave);
  // True while the field has focus. The autosave round-trip echoes the saved text back as a new
  // `value` prop; adopting it mid-edit would overwrite the user's newer keystrokes and snap the caret
  // to the end. So while the user is actively editing, the local draft stays the source of truth and
  // we ignore upstream echoes - external changes (regenerate / AI Suggest) are adopted on blur/idle.
  const focused = React.useRef(false);
  valueRef.current = value;
  draftRef.current = draft;
  onSaveRef.current = onSave;

  // Re-sync the draft when the upstream value changes externally - but never while the user is
  // typing (see `focused`), or the stale save echo reverts their edit and jumps the caret.
  React.useEffect(() => {
    if (!focused.current) setDraft(value);
  }, [value]);

  const doSave = async (text: string) => {
    if (text.trim() === valueRef.current.trim()) return;
    setStatus('saving');
    try {
      await onSaveRef.current(text);
      if (!mounted.current) return;
      setStatus('saved');
      if (hintTimer.current != null) window.clearTimeout(hintTimer.current);
      hintTimer.current = window.setTimeout(() => {
        if (mounted.current) setStatus('idle');
      }, SAVED_HINT_MS);
    } catch {
      if (mounted.current) setStatus('idle');
    }
  };

  const flush = (text: string) => {
    if (saveTimer.current != null) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    void doSave(text);
  };

  const onChange = (text: string) => {
    setDraft(text);
    if (saveTimer.current != null) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => flush(text), AUTOSAVE_DELAY_MS);
  };

  const onFocus = () => {
    focused.current = true;
  };

  const onBlur = () => {
    focused.current = false;
    flush(draftRef.current);
  };

  // Reset `mounted` on every (re)mount: StrictMode's dev mount/unmount/remount would otherwise leave
  // it false after the first cleanup, stranding `status` on 'saving' once a save resolves. The
  // cleanup also flushes a still-pending edit (collapse / navigate-away before the debounce fires).
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (saveTimer.current != null) {
        window.clearTimeout(saveTimer.current);
        saveTimer.current = null;
        if (draftRef.current.trim() !== valueRef.current.trim()) {
          void onSaveRef.current(draftRef.current);
        }
      }
      if (hintTimer.current != null) window.clearTimeout(hintTimer.current);
    };
  }, []);

  return { draft, setDraft, onChange, onFocus, onBlur, flush, status };
}

function EstimateSubsection({
  subsection,
  estimateRange,
  override,
  onOverride,
  onOverrideReason,
  onSummaryEdit,
  onSummarySuggest,
}: {
  subsection: SalesEstimateSubsectionBreakdown;
  estimateRange?: EstimateRange | null;
  override?: EstimateHoursOverride;
  onOverride?: (hours: number | null) => void | Promise<void>;
  onOverrideReason?: (reason: string) => void | Promise<void>;
  /** When provided, the AI/Raw summary text is inline-editable (else rendered read-only). */
  onSummaryEdit?: (patch: SummaryPatch) => void | Promise<void>;
  onSummarySuggest?: SummarySuggest;
}) {
  const canEditSummary = !!onSummaryEdit && !!onSummarySuggest;
  const [isOpen, setIsOpen] = React.useState(false);
  const [expandedRuleKey, setExpandedRuleKey] = React.useState<string | null>(null);
  const [viewMode, setViewMode] = React.useState<'ai' | 'raw' | 'override'>(
    subsection.customer_summary || canEditSummary ? 'ai' : 'raw'
  );
  // The grounding ref whose drill-in is open. Holds the whole ref (not just a uuid) so the dialog can
  // show ONLY the matched golden items + the provenance label, without re-fetching the source sheet.
  const [activeRef, setActiveRef] = React.useState<EstimateGroundingRef | null>(null);
  // Both the AI summary and the override reason auto-save (debounced + on blur); no manual Save.
  const summary = useAutosaveText(subsection.customer_summary ?? '', (t) =>
    onSummaryEdit?.({ customerSummary: t })
  );
  const reason = useAutosaveText(override?.overrideReason ?? '', (r) => onOverrideReason?.(r));
  const [summaryBusy, setSummaryBusy] = React.useState(false);
  const suggestSummary = async () => {
    if (!onSummarySuggest) return;
    setSummaryBusy(true);
    try {
      const next = await onSummarySuggest('customer', summary.draft);
      if (next != null) {
        summary.setDraft(next);
        summary.flush(next);
      }
    } finally {
      setSummaryBusy(false);
    }
  };
  const hasDetail =
    !!subsection.customer_summary ||
    !!subsection.ai_summary ||
    !!subsection.estimationMethod ||
    subsection.firedRules.some((r) => r.calcSummary || r.conditionSummary || r.inputs);
  const isOverridden = override !== undefined;

  // Which views are available: Raw is always the fallback; AI when the summary is editable or
  // present; Override reason only on an active, editable override. `effectiveViewMode` guards a
  // stale selection (e.g. the override was cleared while its tab was active).
  const showAiTab = !!subsection.customer_summary || canEditSummary;
  const showOverrideTab = isOverridden && !!onOverrideReason;
  const showTabs = showAiTab || showOverrideTab;
  const effectiveViewMode =
    viewMode === 'override' && !showOverrideTab
      ? showAiTab
        ? 'ai'
        : 'raw'
      : viewMode === 'ai' && !showAiTab
        ? 'raw'
        : viewMode;
  const activeStatus =
    effectiveViewMode === 'ai'
      ? summary.status
      : effectiveViewMode === 'override'
        ? reason.status
        : 'idle';

  return (
    <div
      className={[
        'sales-estimate-pane__subsection-row',
        isOpen ? 'sales-estimate-pane__subsection-row--open' : null,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="sales-estimate-pane__subsection-main">
        <div className="sales-estimate-pane__subsection-body">
          {hasDetail || showOverrideTab ? (
            <button
              type="button"
              className="sales-estimate-pane__subsection-toggle"
              onClick={() => {
                setIsOpen((o) => !o);
                setExpandedRuleKey(null);
              }}
              aria-expanded={isOpen}
            >
              <span className="sales-estimate-pane__subsection-label">{subsection.label}</span>
            </button>
          ) : (
            <span className="sales-estimate-pane__subsection-label">{subsection.label}</span>
          )}
          {!isOpen && subsection.firedRules.length > 0 ? (
            <span
              className={[
                'sales-estimate-pane__subsection-rationale',
                isOverridden ? 'sales-estimate-pane__subsection-rationale--overridden' : null,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {subsection.firedRules.map((r) => r.description).join(' · ')}
            </span>
          ) : null}
        </div>
        <SectionHoursEdit
          hours={subsection.hours}
          estimateRange={estimateRange}
          override={override}
          onSave={onOverride}
        />
      </div>

      {isOpen ? (
        <div className="sales-estimate-pane__subsection-details">
          {showTabs ? (
            <div className="sales-estimate-pane__summary-header">
              <div className="sales-estimate-pane__subsection-view-toggle">
                {showAiTab ? (
                  <button
                    type="button"
                    className={`sales-estimate-pane__view-btn ${
                      effectiveViewMode === 'ai' ? 'sales-estimate-pane__view-btn--active' : ''
                    }`}
                    onClick={() => setViewMode('ai')}
                  >
                    AI Summary
                  </button>
                ) : null}
                <button
                  type="button"
                  className={`sales-estimate-pane__view-btn ${
                    effectiveViewMode === 'raw' ? 'sales-estimate-pane__view-btn--active' : ''
                  }`}
                  onClick={() => setViewMode('raw')}
                >
                  Raw Output
                </button>
                {showOverrideTab ? (
                  <button
                    type="button"
                    className={`sales-estimate-pane__view-btn ${
                      effectiveViewMode === 'override'
                        ? 'sales-estimate-pane__view-btn--active'
                        : ''
                    }`}
                    onClick={() => setViewMode('override')}
                  >
                    Override reason
                  </button>
                ) : null}
              </div>
              <div className="sales-estimate-pane__summary-header-actions">
                {activeStatus !== 'idle' ? (
                  <span className="sales-estimate-pane__autosave-status">
                    {activeStatus === 'saving' ? 'Saving…' : 'Saved'}
                  </span>
                ) : null}
                {effectiveViewMode === 'ai' && canEditSummary ? (
                  <button
                    type="button"
                    className="sales-estimate-pane__summary-suggest-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      void suggestSummary();
                    }}
                    disabled={summaryBusy}
                  >
                    {summaryBusy ? 'Thinking…' : 'Suggest'}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {effectiveViewMode === 'ai' ? (
            canEditSummary ? (
              <div className="sales-estimate-pane__summary-edit">
                <textarea
                  className="sales-estimate-pane__summary-textarea sales-estimate-pane__summary-textarea--customer"
                  value={summary.draft}
                  placeholder="Client-facing summary - the need, what we'll do, and the key assumption or exclusion."
                  rows={4}
                  onChange={(e) => summary.onChange(e.target.value)}
                  onFocus={summary.onFocus}
                  onBlur={summary.onBlur}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            ) : subsection.customer_summary ? (
              <div className="sales-estimate-pane__ai-summary">{subsection.customer_summary}</div>
            ) : null
          ) : effectiveViewMode === 'override' ? (
            <div className="sales-estimate-pane__summary-edit">
              <textarea
                className="sales-estimate-pane__override-reason"
                value={reason.draft}
                rows={3}
                onChange={(e) => reason.onChange(e.target.value)}
                onFocus={reason.onFocus}
                onBlur={reason.onBlur}
                onClick={(e) => e.stopPropagation()}
                placeholder="Override reason…"
                aria-label="Override reason"
              />
            </div>
          ) : (
            <div className="sales-estimate-pane__raw">
              {/* Status: "mentioned" is context, "ongoing" is deferred - neither is costed. */}
              {subsection.scopeMeta?.scopeStatus === 'mentioned' ? (
                <span className="sales-estimate-pane__scope-badge--context">Context - not scoped</span>
              ) : subsection.scopeMeta?.scopeStatus === 'ongoing' ? (
                <span className="sales-estimate-pane__scope-badge--context">
                  Ongoing discussion - deferred, not scoped yet
                </span>
              ) : null}
              {/* Lead: the technical AI rationale (why these hours). Read-only - only the AI Summary is editable. */}
              {subsection.ai_summary ? (
                <p className="sales-estimate-pane__raw-lead">{subsection.ai_summary}</p>
              ) : null}
              {/* Method + grounded refs, scope interpretation, and the hours Breakdown (aligned def-list). */}
              <EstimateProvenance subsection={subsection} onRefClick={setActiveRef} />
              {subsection.firedRules.length > 0 ? (
                <div className="sales-estimate-pane__rule-list">
                  {subsection.firedRules.map((rule) => {
                const isExpanded = expandedRuleKey === rule.ruleKey;
                const hasRuleDetail =
                  !!rule.calcSummary ||
                  !!rule.conditionSummary ||
                  (rule.inputs !== undefined && Object.keys(rule.inputs).length > 0);
                return (
                  <div
                    key={rule.ruleKey}
                    className={`sales-estimate-pane__rule-item${
                      isExpanded ? ' sales-estimate-pane__rule-item--open' : ''
                    }`}
                  >
                    {hasRuleDetail ? (
                      <button
                        type="button"
                        className="sales-estimate-pane__rule sales-estimate-pane__rule--button"
                        onClick={() =>
                          setExpandedRuleKey((prev) =>
                            prev === rule.ruleKey ? null : rule.ruleKey
                          )
                        }
                        aria-expanded={isExpanded}
                      >
                        <div
                          className={[
                            'sales-estimate-pane__rule-header',
                            isOverridden ? 'sales-estimate-pane__rule-header--overridden' : null,
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <span className="sales-estimate-pane__rule-desc">{rule.description}</span>
                          <span className="sales-estimate-pane__rule-hours">{rule.hours}h</span>
                        </div>
                      </button>
                    ) : (
                      <div className="sales-estimate-pane__rule">
                        <div
                          className={[
                            'sales-estimate-pane__rule-header',
                            isOverridden ? 'sales-estimate-pane__rule-header--overridden' : null,
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <span className="sales-estimate-pane__rule-desc">{rule.description}</span>
                          <span className="sales-estimate-pane__rule-hours">{rule.hours}h</span>
                        </div>
                      </div>
                    )}
                    {isExpanded ? (
                      <EstimateRuleDetail rule={rule} onClose={() => setExpandedRuleKey(null)} />
                    ) : null}
                  </div>
                );
              })}
                </div>
              ) : null}
            </div>
          )}
        </div>
      ) : null}

      <ReferenceEstimateDialog
        groundingRef={activeRef}
        open={activeRef !== null}
        onOpenChange={(o) => {
          if (!o) setActiveRef(null);
        }}
      />
    </div>
  );
}

// ── Business-Process group (two-level ad-hoc scope view) ────────────────────────

/**
 * A coarse Business-Process group within the ad-hoc scope section: a header (group label + the
 * SUMMED effective hours of its members) over its line-item subsections. Renders the two-level view
 * the questionnaire collapses on, while the line items stay the costed/overridable unit (so per-item
 * overrides + the hours-composition drill-in are unchanged). Group hours are recomputed from the
 * EFFECTIVE (post-override) member hours so the subtotal always matches what's shown below it.
 */
function EstimateBusinessProcessGroup({
  group,
  section,
  estimateRange,
  overrides,
  onSubsectionOverride,
  onSubsectionOverrideReason,
  onSubsectionSummaryEdit,
  onSubsectionSummarySuggest,
}: {
  group: { businessProcess: string; hours: number; subsectionKeys: string[] };
  section: SalesEstimateSectionBreakdown;
  estimateRange?: EstimateRange | null;
  overrides: Record<string, EstimateHoursOverride>;
  onSubsectionOverride?: (key: string, hours: number | null) => void | Promise<void>;
  onSubsectionOverrideReason?: (key: string, reason: string) => void | Promise<void>;
  onSubsectionSummaryEdit?: (key: string, patch: SummaryPatch) => void | Promise<void>;
  onSubsectionSummarySuggest?: (
    key: string,
    kind: 'customer' | 'ai',
    text: string
  ) => Promise<string | null>;
}) {
  const members = group.subsectionKeys
    .map((k) => section.subsections.find((s) => s.subsectionKey === k))
    .filter((s): s is NonNullable<typeof s> => s != null);
  if (members.length === 0) return null;
  const groupHours = members.reduce((sum, sub) => {
    const ov = overrides[`subsection:${section.sectionKey}:${sub.subsectionKey}`];
    return sum + (ov ? ov.overrideHours : sub.hours);
  }, 0);
  return (
    <div className="sales-estimate-pane__bp-group">
      <div className="sales-estimate-pane__bp-group-header">
        <span className="sales-estimate-pane__bp-group-label">{group.businessProcess}</span>
        <strong className="sales-estimate-pane__bp-group-hours">
          {renderHours(groupHours, estimateRange)}
        </strong>
      </div>
      {members.map((subsection) => {
        const subKey = `subsection:${section.sectionKey}:${subsection.subsectionKey}`;
        return (
          <EstimateSubsection
            key={subsection.subsectionKey}
            subsection={subsection}
            estimateRange={estimateRange}
            override={overrides[subKey]}
            onOverride={
              onSubsectionOverride ? (hours) => onSubsectionOverride(subKey, hours) : undefined
            }
            onOverrideReason={
              onSubsectionOverrideReason ? (reason) => onSubsectionOverrideReason(subKey, reason) : undefined
            }
            onSummaryEdit={
              onSubsectionSummaryEdit ? (patch) => onSubsectionSummaryEdit(subKey, patch) : undefined
            }
            onSummarySuggest={
              onSubsectionSummarySuggest
                ? (kind, text) => onSubsectionSummarySuggest(subKey, kind, text)
                : undefined
            }
          />
        );
      })}
    </div>
  );
}

// ── Section row ───────────────────────────────────────────────────────────────

function EstimateSection({
  section,
  estimateRange,
  overrides,
  onSubsectionOverride,
  onSubsectionOverrideReason,
  onSubsectionSummaryEdit,
  onSubsectionSummarySuggest,
}: {
  section: SalesEstimateSectionBreakdown;
  estimateRange?: EstimateRange | null;
  overrides: Record<string, EstimateHoursOverride>;
  /** When undefined the subsections are rendered read-only. */
  onSubsectionOverride?: (key: string, hours: number | null) => void | Promise<void>;
  onSubsectionOverrideReason?: (key: string, reason: string) => void | Promise<void>;
  onSubsectionSummaryEdit?: (key: string, patch: SummaryPatch) => void | Promise<void>;
  onSubsectionSummarySuggest?: (
    key: string,
    kind: 'customer' | 'ai',
    text: string
  ) => Promise<string | null>;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const highlights = summarizeFlags(section.flags);

  const isAnySubsectionOverridden = section.subsections.some(
    (sub) => overrides[`subsection:${section.sectionKey}:${sub.subsectionKey}`] !== undefined
  );

  const effectiveSectionHours = React.useMemo(() => {
    if (section.subsections.length === 0) return section.hours;
    return section.subsections.reduce((sum, sub) => {
      const ov = overrides[`subsection:${section.sectionKey}:${sub.subsectionKey}`];
      return sum + (ov ? ov.overrideHours : sub.hours);
    }, 0);
  }, [section, overrides]);

  return (
    <section
      className={`sales-estimate-pane__section${
        isOpen ? ' sales-estimate-pane__section--open' : ''
      }`}
    >
      <div className="sales-estimate-pane__section-row">
        <button
          type="button"
          className="sales-estimate-pane__section-header"
          onClick={() => setIsOpen((o) => !o)}
          aria-expanded={isOpen}
        >
          <div className="sales-estimate-pane__section-title-group">
            <h3 className="sales-estimate-pane__section-title">{section.label}</h3>
            {section.reasons.length > 0 ? (
              <p
                className={[
                  'sales-estimate-pane__section-reason',
                  isAnySubsectionOverridden
                    ? 'sales-estimate-pane__section-reason--overridden'
                    : null,
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {section.reasons[0]}
              </p>
            ) : null}
          </div>
        </button>

        <div className="sales-estimate-pane__section-hours-cell">
          {isAnySubsectionOverridden ? (
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <strong
                  className="sales-estimate-pane__section-hours sales-estimate-pane__section-hours--overridden"
                  style={{ cursor: 'pointer' }}
                >
                  {renderHours(effectiveSectionHours, estimateRange)}
                </strong>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content side="top" sideOffset={4} className="ui-tooltip">
                  Original: {renderHours(section.hours, estimateRange)}
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          ) : (
            <strong className="sales-estimate-pane__section-hours">
              {renderHours(effectiveSectionHours, estimateRange)}
            </strong>
          )}
        </div>
      </div>

      {isOpen ? (
        <>
          {section.subsections.length > 0 ? (
            <div className="sales-estimate-pane__subsection-list">
              {(() => {
                // Two-level when a Business-Process rollup actually groups (some group has >1 line);
                // otherwise flat (legacy/ungrouped sections + singleton groups add no value).
                const rollup = section.businessProcessRollup;
                const grouped = !!rollup && rollup.some((g) => g.subsectionKeys.length > 1);
                if (grouped) {
                  return rollup!.map((grp) => (
                    <EstimateBusinessProcessGroup
                      key={grp.businessProcess}
                      group={grp}
                      section={section}
                      estimateRange={estimateRange}
                      overrides={overrides}
                      onSubsectionOverride={onSubsectionOverride}
                      onSubsectionOverrideReason={onSubsectionOverrideReason}
                      onSubsectionSummaryEdit={onSubsectionSummaryEdit}
                      onSubsectionSummarySuggest={onSubsectionSummarySuggest}
                    />
                  ));
                }
                return section.subsections.map((subsection) => {
                  const subKey = `subsection:${section.sectionKey}:${subsection.subsectionKey}`;
                  return (
                    <EstimateSubsection
                      key={subsection.subsectionKey}
                      subsection={subsection}
                      estimateRange={estimateRange}
                      override={overrides[subKey]}
                      onOverride={
                        onSubsectionOverride
                          ? (hours) => onSubsectionOverride(subKey, hours)
                          : undefined
                      }
                      onOverrideReason={
                        onSubsectionOverrideReason
                          ? (reason) => onSubsectionOverrideReason(subKey, reason)
                          : undefined
                      }
                      onSummaryEdit={
                        onSubsectionSummaryEdit
                          ? (patch) => onSubsectionSummaryEdit(subKey, patch)
                          : undefined
                      }
                      onSummarySuggest={
                        onSubsectionSummarySuggest
                          ? (kind, text) => onSubsectionSummarySuggest(subKey, kind, text)
                          : undefined
                      }
                    />
                  );
                });
              })()}
            </div>
          ) : null}

          {highlights.length > 0 ? (
            <ul className="sales-estimate-pane__flag-list">
              {highlights.map((message) => (
                <li key={message} className="sales-estimate-pane__flag-item">
                  {message}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

// ── Main pane ─────────────────────────────────────────────────────────────────

export function DealEstimatePane({
  result,
  loading,
  error,
  isReady,
  isStale,
  missingRequiredCount,
  resourcePlan,
  savedEstimate,
  onSectionOverride,
  onSectionOverrideReason,
  onSubsectionSummaryEdit,
  onSubsectionSummarySuggest,
  onDiscountOverride,
  onUpdateRolePricingBasis,
  updatingRolePricingBasisId,
  onUpdateRoleAllocation,
  updatingRoleAllocationId,
  availableRoles,
  onRoleSelectionChange,
  updatingRoles,
  regenerating,
  versions,
  selectedVersionUuid,
  onSelectVersion,
  viewingPastVersion,
}: DealEstimatePaneProps) {
  if (loading && !result) {
    return (
      <div
        className="sales-estimate-pane sales-estimate-pane--loading"
        aria-busy="true"
        aria-label="Loading estimate"
      >
        <div className="sales-estimate-pane__summary-grid">
          {[0, 1, 2, 3].map((item) => (
            <SkeletonBlock key={item} className="sales-estimate-pane__summary-skeleton" />
          ))}
        </div>
        <div className="sales-estimate-pane__section-list">
          {[0, 1, 2].map((item) => (
            <SkeletonBlock key={item} className="sales-estimate-pane__section-skeleton" />
          ))}
        </div>
      </div>
    );
  }

  if (error && !result) {
    return renderEmptyState({
      title: 'Estimate unavailable',
      body: error,
      tone: 'danger',
    });
  }

  if (result?.documentAvailability?.estimateAvailable === false) {
    return renderEmptyState({
      title: 'Estimate not configured for this category',
      body: "This deal's category has no estimate configuration. Pick a configured category, or set one up for this category in Products.",
      tone: 'neutral',
    });
  }

  if (!isReady) {
    return renderEmptyState({
      title: 'Estimate locked until questionnaire is complete',
      body:
        missingRequiredCount === 1
          ? 'Complete the remaining required answer to generate the estimate.'
          : `Complete the remaining ${missingRequiredCount} required answers to generate the estimate.`,
      tone: 'neutral',
    });
  }

  if (!result) {
    return renderEmptyState({
      title: 'No estimate generated yet',
      body: 'Use the header action to generate the initial implementation estimate for this deal.',
      tone: 'neutral',
    });
  }

  const { estimate, audit } = result;
  const estimateRange = result.estimateRange ?? null;
  const auditWarnings = audit.missingFieldKeys.length + audit.notes.length;

  // Overrides live in the saved estimate's estimateData, not in the live result.
  const overrides = savedEstimate?.estimateData?.overrides ?? {};
  const hasAnyOverride = Object.keys(overrides).length > 0;

  // Recompute the summary totals using override hours where present so every
  // summary card (grand total AND the Implementation / Testing+Go Live / Post
  // Go Live breakdown) stays in sync with the subsection-level overrides and
  // the cards keep adding up to the grand total.
  const effectiveSummary = React.useMemo(
    () => getEffectiveEstimateData({ ...result, overrides }).summary,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [result, savedEstimate]
  );
  const effectiveGrandTotal = effectiveSummary.grandTotalHours;
  // AI-mode (Continuous Improvement etc.) estimates have no greenfield phase buckets -
  // the breakdown is Project Management + Ad-hoc scope only - so hide the
  // Implementation / Testing+Go Live / Post Go Live summary cards.
  const isAiEstimate = result?.resourcing?.distribution_model === 'ai';

  return (
    <div className="sales-estimate-pane">
      <div className="sales-estimate-pane__meta">
        {SHOW_VERSION_DROPDOWN && versions && versions.length > 1 && onSelectVersion ? (
          <span className="deal-version-select">
            <Select
              value={selectedVersionUuid ?? versions[0]?.uuid ?? ''}
              onValueChange={onSelectVersion}
              size="sm"
            >
              {versions.map((v) => (
                <SelectItem key={v.uuid} value={v.uuid}>
                  {formatDealVersionLabel({
                    version: v.version,
                    timestamp: v.createdAt,
                    totalHours: v.grandTotalHours,
                    isCurrent: v.isCurrent,
                  })}
                </SelectItem>
              ))}
            </Select>
          </span>
        ) : null}
        {viewingPastVersion ? (
          <span className="sales-estimate-pane__meta-item sales-estimate-pane__meta-item--warning">
            Viewing a past version (read-only) - switch to Current to edit
          </span>
        ) : null}
        {savedEstimate ? (
          <span className="sales-estimate-pane__meta-item">
            Saved {formatDateTimeWithYear(savedEstimate.createdAt)}
          </span>
        ) : (
          <span className="sales-estimate-pane__meta-item">
            Generated {formatDateTimeWithYear(estimate.generatedAt)}
          </span>
        )}
        {isStale ? (
          <span className="sales-estimate-pane__meta-item sales-estimate-pane__meta-item--warning">
            Questionnaire changed since last estimate
          </span>
        ) : null}
        {hasAnyOverride ? (
          <span className="sales-estimate-pane__meta-item sales-estimate-pane__meta-item--override">
            Hours overridden
          </span>
        ) : null}
      </div>

      <div className="sales-estimate-pane__summary-grid">
        <article className="sales-estimate-pane__summary-card sales-estimate-pane__summary-card--primary">
          <span className="sales-estimate-pane__summary-label">Grand total</span>
          {hasAnyOverride ? (
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <strong
                  className="sales-estimate-pane__summary-value sales-estimate-pane__summary-value--overridden"
                  style={{ cursor: 'pointer' }}
                >
                  {renderHours(effectiveGrandTotal, estimateRange)}
                </strong>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content side="top" sideOffset={4} className="ui-tooltip">
                  Original: {renderHours(estimate.summary.grandTotalHours, estimateRange)}
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          ) : (
            <strong className="sales-estimate-pane__summary-value">
              {renderHours(effectiveGrandTotal, estimateRange)}
            </strong>
          )}
        </article>
        {!isAiEstimate && (
          <>
            <article className="sales-estimate-pane__summary-card sales-estimate-pane__summary-card--primary">
              <span className="sales-estimate-pane__summary-label">Implementation</span>
              <strong className="sales-estimate-pane__summary-value">
                {renderHours(effectiveSummary.implementationHours, estimateRange)}
              </strong>
            </article>
            <article className="sales-estimate-pane__summary-card sales-estimate-pane__summary-card--primary">
              <span className="sales-estimate-pane__summary-label">Testing + Go Live</span>
              <strong className="sales-estimate-pane__summary-value">
                {renderHours(
                  effectiveSummary.testingHours + effectiveSummary.goLiveHours,
                  estimateRange
                )}
              </strong>
            </article>
            <article className="sales-estimate-pane__summary-card sales-estimate-pane__summary-card--primary">
              <span className="sales-estimate-pane__summary-label">Post Go Live</span>
              <strong className="sales-estimate-pane__summary-value">
                {renderHours(effectiveSummary.postGoLiveHours, estimateRange)}
              </strong>
            </article>
          </>
        )}
      </div>

      {auditWarnings > 0 ? (
        <div className="sales-estimate-pane__audit-banner" role="status">
          <span className="sales-estimate-pane__audit-title">Audit notes</span>
          <span className="sales-estimate-pane__audit-text">
            {auditWarnings} follow-up {auditWarnings === 1 ? 'item' : 'items'} were captured while
            building this estimate.
          </span>
        </div>
      ) : null}

      <div className="sales-estimate-pane__section-list">
        {estimate.sections.map((section) => (
          <EstimateSection
            key={section.sectionKey}
            section={section}
            estimateRange={estimateRange}
            overrides={overrides}
            onSubsectionOverride={
              onSectionOverride && savedEstimate
                ? (key, hours) => onSectionOverride(key, hours)
                : undefined
            }
            onSubsectionOverrideReason={
              onSectionOverrideReason
                ? (key, reason) => onSectionOverrideReason(key, reason)
                : undefined
            }
            onSubsectionSummaryEdit={
              onSubsectionSummaryEdit && savedEstimate
                ? (key, patch) => onSubsectionSummaryEdit(key, patch)
                : undefined
            }
            onSubsectionSummarySuggest={
              onSubsectionSummarySuggest && savedEstimate
                ? (key, kind, text) => onSubsectionSummarySuggest(key, kind, text)
                : undefined
            }
          />
        ))}
      </div>

      {resourcePlan && (
        <>
          <ResourceInvestmentTable
            plan={resourcePlan}
            hideWeeklyAvgColumn
            regenerating={regenerating}
            onUpdateRolePricingBasis={onUpdateRolePricingBasis}
            updatingRolePricingBasisId={updatingRolePricingBasisId}
            onUpdateRoleAllocation={onUpdateRoleAllocation}
            updatingRoleAllocationId={updatingRoleAllocationId}
            discountPercentageOverride={result?.discountPercentageOverride ?? null}
            defaultDiscountPercentage={result?.discountPercentage ?? null}
            onDiscountOverride={onDiscountOverride}
            availableRoles={availableRoles}
            onRoleSelectionChange={onRoleSelectionChange}
            updatingRoles={updatingRoles}
          />
        </>
      )}

      {result && (
        <ResourcingModelSummary
          resourcing={result.resourcing}
          resourcePlan={resourcePlan}
          discountPercentageOverride={result?.discountPercentageOverride ?? null}
          defaultDiscountPercentage={result?.discountPercentage ?? null}
        />
      )}
    </div>
  );
}

export function DealEstimateListPane({
  estimateResult,
  loading,
  savedEstimate,
  isCurrent,
  onViewEstimate,
}: DealEstimateListPaneProps) {
  if (loading && !estimateResult) {
    return (
      <div className="sales-estimate-list-pane" aria-busy="true" aria-label="Loading estimate">
        <SkeletonBlock className="sales-estimate-list-pane__skeleton" />
      </div>
    );
  }

  if (!estimateResult) {
    return renderEmptyState({
      title: 'No estimate generated yet',
      body: 'Use the Generate button to create the initial implementation estimate for this deal.',
      tone: 'neutral',
    });
  }

  const { estimate } = estimateResult;
  const estimateRange = estimateResult.estimateRange ?? null;

  return (
    <div className="sales-estimate-list-pane">
      <button type="button" className="sales-estimate-list-pane__row" onClick={onViewEstimate}>
        <div className="sales-estimate-list-pane__row-body">
          <div className="sales-estimate-list-pane__row-header">
            <div className="sales-estimate-list-pane__row-total">
              {renderHours(estimate.summary.grandTotalHours, estimateRange)}
              <span className="sales-estimate-list-pane__row-total-label">grand total</span>
            </div>
            {isCurrent ? (
              <span className="sales-estimate-list-pane__row-badge">Current</span>
            ) : null}
          </div>
          <div className="sales-estimate-list-pane__row-meta">
            <span>Generated {formatDateTimeWithYear(estimate.generatedAt)}</span>
            {savedEstimate ? (
              <span>Saved {formatDateTimeWithYear(savedEstimate.createdAt)}</span>
            ) : (
              <span className="sales-estimate-list-pane__row-unsaved">Unsaved</span>
            )}
          </div>
        </div>
      </button>
    </div>
  );
}
